export type ReportPeriod = "week" | "month" | "year";

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

export function toYMD(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function startOfMondayWeek(d: Date): Date {
  const day = d.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const monday = new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate() + mondayOffset,
  );
  monday.setHours(0, 0, 0, 0);
  return monday;
}

export interface ReportPeriodBounds {
  start: string;
  end: string;
  /** Human-readable range for the UI */
  label: string;
}

/**
 * Inclusive YYYY-MM-DD bounds for the report. Weeks start on Monday.
 * `offset` shifts by whole weeks / months / years from the period that contains `reference`.
 */
export function getReportPeriodBounds(
  period: ReportPeriod,
  offset: number,
  reference: Date = new Date(),
): ReportPeriodBounds {
  const ref = new Date(
    reference.getFullYear(),
    reference.getMonth(),
    reference.getDate(),
  );

  if (period === "year") {
    const y = ref.getFullYear() + offset;
    return {
      start: `${y}-01-01`,
      end: `${y}-12-31`,
      label: String(y),
    };
  }

  if (period === "month") {
    const base = new Date(ref.getFullYear(), ref.getMonth() + offset, 1);
    const y = base.getFullYear();
    const m = base.getMonth();
    const last = new Date(y, m + 1, 0);
    return {
      start: toYMD(new Date(y, m, 1)),
      end: toYMD(last),
      label: base.toLocaleString(undefined, { month: "long", year: "numeric" }),
    };
  }

  const monday = startOfMondayWeek(ref);
  monday.setDate(monday.getDate() + 7 * offset);
  const sunday = new Date(
    monday.getFullYear(),
    monday.getMonth(),
    monday.getDate() + 6,
  );
  const start = toYMD(monday);
  const end = toYMD(sunday);
  return {
    start,
    end,
    label: `${start} – ${end}`,
  };
}

export function filterEntriesByYmdRange<T extends { spentAt: string }>(
  entries: T[],
  start: string,
  end: string,
): T[] {
  return entries.filter((e) => e.spentAt >= start && e.spentAt <= end);
}

export function eachDayYmdInRange(startYmd: string, endYmd: string): string[] {
  const [ys, ms, ds] = startYmd.split("-").map(Number);
  const [ye, me, de] = endYmd.split("-").map(Number);
  const out: string[] = [];
  const cur = new Date(ys, ms - 1, ds);
  const end = new Date(ye, me - 1, de);
  while (cur <= end) {
    out.push(toYMD(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

export function eachMonthYmInRange(startYmd: string, endYmd: string): string[] {
  const startYm = startYmd.slice(0, 7);
  const endYm = endYmd.slice(0, 7);
  const out: string[] = [];
  let [y, m] = startYm.split("-").map(Number);
  const [ye, me] = endYm.split("-").map(Number);
  while (y < ye || (y === ye && m <= me)) {
    out.push(`${y}-${pad2(m)}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

export function formatTrendAxisLabel(
  bucketKey: string,
  period: ReportPeriod,
): string {
  const [y, mo, d] = bucketKey.split("-").map(Number);
  if (period === "year") {
    return new Date(y, mo - 1, 1).toLocaleString(undefined, { month: "short" });
  }
  if (period === "week") {
    return new Date(y, mo - 1, d).toLocaleString(undefined, {
      weekday: "short",
    });
  }
  return new Date(y, mo - 1, d).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
  });
}
